import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import React, { useState } from 'react';
import { useHrpsApi } from '../../clients/useHrpsApi';
import { useViewClaimDocument } from '../../hooks/useViewClaimDocument';
import { DocumentAttachment } from '../../types';
import { truncateWithPopover } from '../../utils/textUtils';
import { useTranslation } from '../blocks';
import { useModal } from './Modal';
import { usePageMessaging } from './PageMessaging';

interface ClaimDocumentTableProps {
    resellerId: string;
    resellerClaimNumber: string;
    documents: DocumentAttachment[];
    onDocumentDeleted: (docId: string) => void;
}

export const ClaimDocumentTable = ({
    resellerId,
    resellerClaimNumber,
    documents,
    onDocumentDeleted,
}: ClaimDocumentTableProps) => {
    const {
        claims: { removeAttachmentFromClaim },
    } = useHrpsApi();
    const { t } = useTranslation('claimDocuments');
    const { t: tCommon } = useTranslation('common');
    const modal = useModal();
    const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
    const pageMessaging = usePageMessaging();
    const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
    const downloadDocument = useViewClaimDocument();

    const handleDeleteDocument = async (doc: DocumentAttachment) => {
        modal
            .confirm(
                t('claim-document-delete-title'),
                t('claim-document-delete-confirmation', { documentName: doc.attachmentName || 'this document' }),
                t('claim-document-delete-confirm-button'),
                t('claim-document-delete-cancel-button')
            )
            .applied(async () => {
                setDeletingDocId(doc.attachmentId);
                try {
                    await removeAttachmentFromClaim({
                        resellerId,
                        resellerClaimNumber,
                        attachmentId: doc.attachmentId,
                    });

                    onDocumentDeleted(doc.attachmentId);
                    pageMessaging.showSuccess(t('claim-document-delete-success'));
                } catch (error) {
                    pageMessaging.showError(t('claim-document-delete-error'));
                } finally {
                    setDeletingDocId(null);
                }
            });
    };

    const handleDownloadDocument = async (doc: DocumentAttachment) => {
        setDownloadingDocId(doc.attachmentId);
        try {
            await downloadDocument(resellerId, doc.attachmentId);
        } finally {
            setDownloadingDocId(null);
        }
    };

    return (
        <Box type={'outline'} spacingInset={'none'} width={'100%'}>
            <Box spacingInset={'medium'} backgroundColor={'secondary'}>
                <Text type={'h200'}>{t('claim-document-table-title')}</Text>
            </Box>
            <Box spacingInset={'500'} overflowX={'auto'}>
                <Row alignmentVertical={'stretch'}>
                    <Table headerRows={1} spacing={'small'}>
                        <TableRow>
                            <TableCell>{t('claim-document-table-name-header')}</TableCell>
                            <TableCell>{t('claim-document-table-submitted-by-header')}</TableCell>
                            <TableCell>{t('claim-document-table-actions-header')}</TableCell>
                        </TableRow>
                        {documents.length > 0 ? (
                            documents.map((doc) => (
                                <TableRow key={doc.attachmentId} highlightOnHover={true}>
                                    <TableCell alignmentHorizontal={'center'}>
                                        <Button
                                            type={'link'}
                                            onClick={() => handleDownloadDocument(doc)}
                                            disabled={downloadingDocId === doc.attachmentId}
                                        >
                                            {downloadingDocId === doc.attachmentId ? (
                                                <Loader size={'small'} />
                                            ) : (
                                                truncateWithPopover(
                                                    doc.attachmentName || t('claim-document-table-unknown-name')
                                                )
                                            )}
                                        </Button>
                                    </TableCell>
                                    <TableCell>{doc.attachmentUser}</TableCell>
                                    <TableCell alignmentHorizontal={'center'}>
                                        {deletingDocId === doc.attachmentId ? (
                                            <Row alignmentHorizontal={'center'}>
                                                <Loader size={'small'} />
                                            </Row>
                                        ) : (
                                            <Button type={'secondary'} onClick={() => handleDeleteDocument(doc)}>
                                                {tCommon('delete-text')}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell columnSpan={3} alignmentHorizontal={'center'}>
                                    <Column spacingInset={'medium'}>
                                        <Text type={'b300'}>{t('claim-document-table-empty-message')}</Text>
                                    </Column>
                                </TableCell>
                            </TableRow>
                        )}
                    </Table>
                </Row>
            </Box>
        </Box>
    );
};
